import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Divider,
  Alert,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment
} from '@mui/material';
import { FormField, creamBg, filledInputSx } from '../components/FormDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LinkIcon from '@mui/icons-material/Link';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

import QuotaBadge from '../components/QuotaBadge';
import { useTranslation } from 'react-i18next';

const arborGreen = '#0A2A21';


const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultDates = () => {
  const today = new Date();
  return {
    start: formatDateInput(new Date(today.getFullYear() - 1, today.getMonth(), 1)),
    end: formatDateInput(today),
  };
};

export default function Documents() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const initialDates = getDefaultDates();

  // State variables
  const [documents, setDocuments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [docType, setDocType] = useState('ALL');
  const [startDate, setStartDate] = useState(initialDates.start);
  const [endDate, setEndDate] = useState(initialDates.end);
  const [activeFilter, setActiveFilter] = useState('1 Year');
  const [page, setPage] = useState(0);
  const limit = 12;

  // Selected document for preview/detail
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [fileBlobUrl, setFileBlobUrl] = useState(null);
  const [filePreviewLoading, setFilePreviewLoading] = useState(false);
  const [viewMode, setViewMode] = useState('items'); // 'items' or 'json'
  const [zoomOpen, setZoomOpen] = useState(false);

  // Deletion dialog states
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [cascadeDelete, setCascadeDelete] = useState(false);
  const [understandChecked, setUnderstandChecked] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch documents
  const fetchDocuments = useCallback(async (resetPage = false) => {
    setLoading(true);
    try {
      const activePage = resetPage ? 0 : page;
      if (resetPage) setPage(0);

      const params = {
        skip: activePage * limit,
        limit: limit,
      };

      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (docType !== 'ALL') params.document_type = docType;
      if (startDate) params.start_date = new Date(startDate).toISOString();
      if (endDate) {
        const end = new Date(endDate);
        end.setUTCHours(23, 59, 59, 999);
        params.end_date = end.toISOString();
      }

      const response = await api.get('/source-documents/', { params });
      setDocuments(response.data.items);
      setTotalCount(response.data.total);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch source documents:', err);
      setError(t('Failed to fetch source documents. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [api, page, debouncedSearch, docType, startDate, endDate, t]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleResetFilters = () => {
    setSearch('');
    setDocType('ALL');
    setStartDate(initialDates.start);
    setEndDate(initialDates.end);
    setActiveFilter('1 Year');
    setPage(0);
  };

  // Preview Dialog trigger
  const handleOpenDetail = async (doc) => {
    setSelectedDoc(doc);
    setDetailOpen(true);
    setFilePreviewLoading(true);
    setFileBlobUrl(null);
    setViewMode('items');
    try {
      const response = await api.get(`/source-documents/${doc.id}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      setFileBlobUrl(url);
    } catch (err) {
      console.error('Failed to load document preview file:', err);
      showToast(t('Could not load document preview file. Please try again.'), 'error');
    } finally {
      setFilePreviewLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedDoc(null);
    setViewMode('items');
    if (fileBlobUrl) {
      URL.revokeObjectURL(fileBlobUrl);
      setFileBlobUrl(null);
    }
  };

  // Delete Dialog trigger
  const handleOpenDelete = (doc, e) => {
    e.stopPropagation();
    setDocToDelete(doc);
    setCascadeDelete(false);
    setUnderstandChecked(false);
    setDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteOpen(false);
    setDocToDelete(null);
    setCascadeDelete(false);
    setUnderstandChecked(false);
  };

  const handleDeleteSubmit = async () => {
    if (docToDelete.ledger_entries?.length > 0 && !understandChecked) {
      showToast(t('Please confirm you understand the deletion impact by checking the validation checkbox.'), 'warning');
      return;
    }

    setDeleting(true);
    try {
      await api.delete(`/source-documents/${docToDelete.id}`, {
        params: { delete_transactions: cascadeDelete }
      });
      showToast(t('Document deleted successfully.'), 'success');
      handleCloseDelete();
      // If we deleted the open detail doc, close it too
      if (selectedDoc?.id === docToDelete.id) {
        handleCloseDetail();
      }
      fetchDocuments();
    } catch (err) {
      console.error('Failed to delete document:', err);
      if (err.response?.data?.detail) {
        showToast(err.response.data.detail, 'error');
      } else {
        showToast(t('Failed to delete document. Please try again.'), 'error');
      }
    } finally {
      setDeleting(false);
    }
  };

  const formatAmount = (amt) => {
    if (amt === undefined || amt === null) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amt);
  };

  const needsReview = (doc) => {
    const parsed = doc.parsed_json || {};
    const total = parseFloat(parsed.total || 0);
    const store = parsed.name || '';
    return total <= 0 || !store.trim() || store.toLowerCase() === 'unknown';
  };

  const hasLinkedTransactions = (doc) => {
    return doc.ledger_entries && doc.ledger_entries.length > 0;
  };

  // Total pages calculation
  const totalPages = Math.ceil(totalCount / limit);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }} id="documents-page-root">
      {/* Title block */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/')} color="primary" sx={{ border: '1px solid #ddd' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: arborGreen, letterSpacing: '-0.02em' }}>
              {t('Document Library')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('View, search, audit, and manage your receipts and invoice database.')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ minWidth: 240 }}>
          <QuotaBadge featureKey="ai_document_scan" />
        </Box>
      </Box>


      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          bgcolor: creamBg,
          borderRadius: (theme) => `${theme.shape?.containerRadius || 32}px`,
          border: '1px solid rgba(10, 42, 33, 0.08)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: { xs: 'stretch', md: 'flex-end' } }}>
          <Box sx={{ display: 'flex', gap: 2, flex: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
            <FormField label={t('From')} sx={{ flex: 1 }}>
              <TextField
                type="date"
                variant="filled"
                InputLabelProps={{ shrink: true }}
                InputProps={{ disableUnderline: true }}
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setActiveFilter('Custom');
                  setPage(0);
                }}
                fullWidth
                sx={filledInputSx}
              />
            </FormField>
            <FormField label={t('To')} sx={{ flex: 1 }}>
              <TextField
                type="date"
                variant="filled"
                InputLabelProps={{ shrink: true }}
                InputProps={{ disableUnderline: true }}
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setActiveFilter('Custom');
                  setPage(0);
                }}
                fullWidth
                sx={filledInputSx}
              />
            </FormField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flex: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
            <FormField label={t('Search')} sx={{ flex: 1 }}>
              <TextField
                variant="filled"
                placeholder={t('Vendor, category...')}
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                fullWidth
                sx={filledInputSx}
              />
            </FormField>
            <FormField label={t('Document Type')} sx={{ flex: 1 }}>
              <TextField
                select
                variant="filled"
                value={docType}
                onChange={(e) => {
                  setDocType(e.target.value);
                  setPage(0);
                }}
                InputProps={{ disableUnderline: true }}
                fullWidth
                sx={filledInputSx}
              >
                <MenuItem value="ALL">{t('All Types')}</MenuItem>
                <MenuItem value="RECEIPT">{t('Receipt')}</MenuItem>
                <MenuItem value="INVOICE">{t('Invoice')}</MenuItem>
                <MenuItem value="BANK_STATEMENT">{t('Bank Statement')}</MenuItem>
              </TextField>
            </FormField>
          </Box>
          <Button
            variant="text"
            color="primary"
            onClick={handleResetFilters}
            sx={{
              height: 56,
              px: 3,
              ml: { xs: 0, md: 'auto' },
              fontWeight: 700,
              color: arborGreen,
              opacity: 0.7,
              '&:hover': { opacity: 1, bgcolor: 'rgba(10, 42, 33, 0.05)' }
            }}
          >
            {t('Reset')}
          </Button>
        </Box>

        <Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
            {[
              { label: 'This Month', calc: () => { const now = new Date(); return new Date(now.getFullYear(), now.getMonth(), 1); } },
              { label: '1 Month', calc: () => { const now = new Date(); return new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()); } },
              { label: '3 Months', calc: () => { const now = new Date(); return new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()); } },
              { label: '6 Months', calc: () => { const now = new Date(); return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()); } },
              { label: '1 Year', calc: () => { const now = new Date(); return new Date(now.getFullYear() - 1, now.getMonth(), 1); } },
              { label: '3 Years', calc: () => { const now = new Date(); return new Date(now.getFullYear() - 3, now.getMonth(), 1); } },
              { label: 'All Years', calc: null },
            ].map((filter) => (
              <Button
                key={filter.label}
                variant="filter"
                color={activeFilter === filter.label ? 'primary' : 'inherit'}
                onClick={() => {
                  if (filter.calc) {
                    const end = new Date();
                    const start = filter.calc();
                    setStartDate(formatDateInput(start));
                    setEndDate(formatDateInput(end));
                  } else {
                    setStartDate('');
                    setEndDate('');
                  }
                  setActiveFilter(filter.label);
                  setPage(0);
                }}
              >
                {t(filter.label)}
              </Button>
            ))}
          </Stack>
        </Box>
      </Paper>

      {/* Main Grid area */}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress color="success" />
        </Box>
      ) : documents.length === 0 ? (
        <Paper elevation={0} sx={{ py: 8, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 3 }}>
          <FilePresentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('No Documents Found')}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {t('Try adjusting your search criteria, category filters, or select a different date range.')}
          </Typography>
        </Paper>
      ) : (
        <>
          <Grid container spacing={3}>
            {documents.map((doc) => {
              const reviewRequired = needsReview(doc);
              const storeName = doc.parsed_json?.name || t('Unknown Store');
              const docTotal = doc.parsed_json?.total || 0;
              const docDate = doc.parsed_json?.date || doc.created_at;
              const hasLinks = hasLinkedTransactions(doc);

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
                  <Card
                    onClick={() => handleOpenDetail(doc)}
                    sx={{
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      border: '1px solid #E8EBF0',
                      boxShadow: 'none',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                        borderColor: arborGreen
                      }
                    }}
                    id={`doc-card-${doc.id}`}
                  >
                    <CardContent sx={{ flexGrow: 1, position: 'relative', pt: 3 }}>
                      {/* Badge Top Corner */}
                      <Stack direction="row" spacing={1} sx={{ position: 'absolute', top: 12, right: 12 }}>
                        {reviewRequired && (
                          <Chip
                            icon={<WarningAmberIcon style={{ fontSize: 14, color: '#A04000' }} />}
                            label={t('Needs Review')}
                            size="small"
                            sx={{
                              bgcolor: '#FEF9E7',
                              color: '#A04000',
                              border: '1px solid #FADBD8',
                              fontWeight: 600,
                              fontSize: 10
                            }}
                          />
                        )}
                        {hasLinks && (
                          <Chip
                            icon={<LinkIcon style={{ fontSize: 14, color: arborGreen }} />}
                            label={`${doc.ledger_entries.length} ${t('Linked')}`}
                            size="small"
                            sx={{
                              bgcolor: '#E8F8F5',
                              color: arborGreen,
                              fontWeight: 600,
                              fontSize: 10
                            }}
                          />
                        )}
                      </Stack>

                      {/* Header info */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                        <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#EAF2F0', color: arborGreen, display: 'flex' }}>
                          <ReceiptIcon />
                        </Box>
                        <Box sx={{ maxWidth: '60%' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>
                            {t(doc.document_type) || doc.document_type}
                          </Typography>
                          <Typography variant="body1" noWrap sx={{ fontWeight: 700, color: '#333' }}>
                            {storeName}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      {/* Financial Detail */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {t('Total Amount')}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: arborGreen }}>
                            {formatAmount(docTotal)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {t('Date')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#555', mt: 0.5 }}>
                            {new Date(docDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </Typography>
                        </Box>
                      </Box>

                      {doc.parsed_json?.category && (
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label={t(doc.parsed_json.category) || doc.parsed_json.category}
                            size="small"
                            sx={{ bgcolor: '#F1F3F5', color: '#555', fontWeight: 600, fontSize: 11 }}
                          />
                        </Box>
                      )}
                    </CardContent>

                    {/* Bottom toolbar */}
                    <Box
                      sx={{
                        p: 1.5,
                        pt: 0,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: '#FAFBFB',
                        borderTop: '1px solid #F0F2F5',
                        borderBottomLeftRadius: 12,
                        borderBottomRightRadius: 12
                      }}
                    >
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        sx={{ color: arborGreen, fontWeight: 600, textTransform: 'none' }}
                      >
                        {t('Inspect & Review')}
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => handleOpenDelete(doc, e)}
                        sx={{ '&:hover': { bgcolor: '#FADBD8' } }}
                        id={`delete-btn-${doc.id}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 6, gap: 2 }}>
              <Button
                variant="outlined"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
                sx={{ borderRadius: 2, textTransform: 'none', color: arborGreen, borderColor: arborGreen }}
              >
                {t('Previous')}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {t('Page {{current}} of {{total}}', { current: page + 1, total: totalPages })}
              </Typography>
              <Button
                variant="outlined"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
                sx={{ borderRadius: 2, textTransform: 'none', color: arborGreen, borderColor: arborGreen }}
              >
                {t('Next')}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* DETAIL / PREVIEW DIALOG */}
      <Dialog
        open={detailOpen}
        onClose={handleCloseDetail}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 4,
            height: { xs: 'auto', md: '80vh' },
            maxHeight: { xs: '90vh', md: '80vh' }
          }
        }}
        id="document-detail-dialog"
      >
        {selectedDoc && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: arborGreen, color: '#fff', py: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t('Review Source Document:')} {selectedDoc.parsed_json?.name || t('Unknown Store')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#BDC3C7' }}>
                  {t('Uploaded')} {new Date(selectedDoc.created_at).toLocaleString()}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseDetail} sx={{ color: '#fff' }}>
                &times;
              </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0, overflow: "hidden" }}>
              <Grid container sx={{ height: { xs: 'auto', md: '100%' }, overflow: 'hidden' }}>
                <Grid
                  size={{ xs: 12, md: 3 }}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: { xs: 'auto', md: '100%' },
                    borderRight: '1px solid #E8EBF0'
                  }}
                >
                  {/* PDF/Image Preview Side (Left side - 1/4 width, fixed) */}
                  <Box sx={{ p: 2, bgcolor: '#F8F9FA', borderBottom: '1px solid #E8EBF0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FilePresentIcon sx={{ fontSize: 18 }} /> {t('Preview')}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!fileBlobUrl}
                      onClick={() => fileBlobUrl && window.open(fileBlobUrl, '_blank')}
                      sx={{ textTransform: 'none', color: arborGreen, borderColor: arborGreen, borderRadius: 2, fontSize: '0.7rem', px: 1, py: 0.25 }}
                    >
                      {t('New Tab')}
                    </Button>
                  </Box>
                  <Box sx={{ flexGrow: 1, p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#FAFAFA', overflow: 'hidden', position: 'relative', height: 'calc(100% - 53px)', minHeight: 0 }}>
                    {filePreviewLoading ? (
                      <Stack spacing={2} alignItems="center">
                        <CircularProgress color="success" />
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontSize: '0.75rem' }}>{t('Loading preview...')}</Typography>
                      </Stack>
                    ) : !fileBlobUrl ? (
                      <Stack spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                        <WarningAmberIcon sx={{ fontSize: 40, color: '#A04000' }} />
                        <Typography variant="body2" sx={{ textAlign: 'center', fontSize: '0.75rem' }}>{t('Failed to load preview.')}</Typography>
                      </Stack>
                    ) : selectedDoc.file_type?.startsWith('image/') ? (
                      <Box
                        onClick={() => setZoomOpen(true)}
                        sx={{
                          position: 'relative',
                          cursor: 'pointer',
                          maxWidth: '100%',
                          height: '100%',
                          borderRadius: 2,
                          overflow: 'hidden',
                          boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                          border: '1px solid #E8EBF0',
                          transition: 'all 0.25s ease',
                          bgcolor: '#fff',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
                            '& .magnifier-overlay': { opacity: 1 }
                          }
                        }}
                      >
                        <img
                          src={fileBlobUrl}
                          alt="Document Preview"
                          style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' }}
                        />
                        <Box
                          className="magnifier-overlay"
                          sx={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            bgcolor: 'rgba(10, 42, 33, 0.72)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            opacity: 0,
                            transition: 'opacity 0.2s ease',
                            color: '#fff',
                            gap: 0.5
                          }}
                        >
                          <ZoomInIcon sx={{ fontSize: 28 }} />
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem', letterSpacing: 0.5 }}>
                            {t('Click to Zoom')}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <iframe
                        src={fileBlobUrl}
                        title="Document Preview Frame"
                        width="100%"
                        height="100%"
                        style={{ border: 'none', borderRadius: 8, minHeight: '300px' }}
                      />
                    )}
                  </Box>
                </Grid>
                <Grid
                  size={{ xs: 12, md: 9 }}
                  sx={{
                    p: 3,
                    overflowY: { xs: 'visible', md: 'auto' },
                    height: { xs: 'auto', md: '100%' }
                  }}
                >
                  {/* Audit details side (Right side - 3/4 width, scrollable) */}
                  {needsReview(selectedDoc) && (
                    <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('Needs Data Validation')}</Typography>
                      {t('The AI scan detected a zero-total or missing vendor name. Please cross-reference with the attachment.')}
                    </Alert>
                  )}

                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('Parsed Document Metadata')}
                  </Typography>

                  <Paper elevation={0} sx={{ p: 2, border: '1px solid #E8EBF0', borderRadius: 3, mb: 3 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={3}>
                        <Typography variant="caption" color="text.secondary">{t('Store Vendor')}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#333' }}>
                          {selectedDoc.parsed_json?.name || t('Unknown')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">{t('Transaction Date')}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                          {selectedDoc.parsed_json?.date || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">{t('Total Amount')}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 800, color: arborGreen }}>
                          {formatAmount(selectedDoc.parsed_json?.total)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">{t('Tax Amount')}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                          {formatAmount(selectedDoc.parsed_json?.tax)}
                        </Typography>
                      </Grid>
                      {selectedDoc.parsed_json?.payment_method && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">{t('Payment Method')}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {selectedDoc.parsed_json.payment_method}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>

                  {/* Receipt Items & JSON toggle */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('Extracted Details')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, bgcolor: '#F0F3F4', p: 0.5, borderRadius: 2 }}>
                        <Button
                          size="small"
                          onClick={() => setViewMode('items')}
                          sx={{
                            textTransform: 'none',
                            borderRadius: 1.5,
                            px: 2,
                            py: 0.25,
                            fontSize: '0.75rem',
                            fontWeight: viewMode === 'items' ? 700 : 500,
                            bgcolor: viewMode === 'items' ? '#fff' : 'transparent',
                            color: viewMode === 'items' ? arborGreen : '#7F8C8D',
                            boxShadow: viewMode === 'items' ? '0 2px 6px rgba(0,0,0,0.08)' : 'none',
                            '&:hover': { bgcolor: viewMode === 'items' ? '#fff' : '#E5E8E8' }
                          }}
                        >
                          {t('Line Items')}
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setViewMode('json')}
                          sx={{
                            textTransform: 'none',
                            borderRadius: 1.5,
                            px: 2,
                            py: 0.25,
                            fontSize: '0.75rem',
                            fontWeight: viewMode === 'json' ? 700 : 500,
                            bgcolor: viewMode === 'json' ? '#fff' : 'transparent',
                            color: viewMode === 'json' ? arborGreen : '#7F8C8D',
                            boxShadow: viewMode === 'json' ? '0 2px 6px rgba(0,0,0,0.08)' : 'none',
                            '&:hover': { bgcolor: viewMode === 'json' ? '#fff' : '#E5E8E8' }
                          }}
                        >
                          {t('Raw JSON')}
                        </Button>
                      </Box>
                    </Box>

                    {viewMode === 'items' ? (
                      selectedDoc.parsed_json?.items?.length > 0 ? (
                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E8EBF0', borderRadius: 3 }}>
                          <Table size="small">
                            <TableHead sx={{ bgcolor: '#F8F9FA' }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>{t('Item')}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>{t('Price')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedDoc.parsed_json.items.map((item, idx) => {
                                const itemName = item.item || item.name || `${t('Line')} ${idx + 1}`;
                                const itemPrice = item.amount !== undefined ? item.amount : item.price;
                                return (
                                  <TableRow key={idx}>
                                    <TableCell>{itemName}</TableCell>
                                    <TableCell align="right">{formatAmount(itemPrice)}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>{t('No itemized line items extracted.')}</Alert>
                      )
                    ) : (
                      <Box sx={{
                        p: 2,
                        bgcolor: '#1E1E1E',
                        color: '#00FF66',
                        borderRadius: 3,
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        overflowX: 'auto',
                        maxHeight: '300px',
                        border: '1px solid #333',
                        boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.5)'
                      }}>
                        <pre style={{ margin: 0 }}>
                          {JSON.stringify(selectedDoc.parsed_json, null, 2)}
                        </pre>
                      </Box>
                    )}
                  </Box>

                  {/* Associated Ledger entries / Transactions */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinkIcon sx={{ color: arborGreen }} /> {t('Linked Transactions')}
                    </Typography>

                    {!hasLinkedTransactions(selectedDoc) ? (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        {t('This document is currently not linked to any transactions. Link it inside the Transactions page during matching or uploads.')}
                      </Alert>
                    ) : (
                      <Stack spacing={1.5}>
                        {selectedDoc.ledger_entries.map((entry) => (
                          <Paper
                            key={entry.id}
                            elevation={0}
                            sx={{
                              p: 2,
                              border: '1px solid #E8EBF0',
                              borderRadius: 3,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              '&:hover': { bgcolor: '#FDFEFE' }
                            }}
                          >
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {entry.description}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {t('Ledger ID:')} {entry.ledger_id} • {new Date(entry.date).toLocaleDateString()}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ fontWeight: 800, color: entry.amount >= 0 ? '#196F3D' : '#943126' }}>
                                {entry.amount >= 0 ? '+' : ''}{formatAmount(entry.amount)}
                              </Typography>
                              <Button
                                size="small"
                                onClick={() => {
                                  navigate(`/ledger/${entry.ledger_id}`);
                                  handleCloseDetail();
                                }}
                                sx={{ textTransform: 'none', fontSize: 11, p: 0, minWidth: 0, mt: 0.5, color: arborGreen }}
                              >
                                {t('View Ledger')}
                              </Button>
                            </Box>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 2.5, justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={(e) => handleOpenDelete(selectedDoc, e)}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                {t('Delete Document')}
              </Button>
              <Button
                variant="contained"
                onClick={handleCloseDetail}
                sx={{ bgcolor: arborGreen, '&:hover': { bgcolor: '#071F18' }, borderRadius: 2, px: 4, textTransform: 'none' }}
              >
                {t('Done')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* CONFIRMATION / SAFE DELETION DIALOG */}
      <Dialog
        open={deleteOpen}
        onClose={handleCloseDelete}
        PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
        id="document-delete-dialog"
      >
        {docToDelete && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
              <WarningAmberIcon color="error" sx={{ fontSize: 32 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t('Confirm Safe Document Deletion')}
              </Typography>
            </DialogTitle>

            <DialogContent>
              <DialogContentText sx={{ mb: 2, color: '#555' }}>
                {t('You are about to delete')} <strong>{docToDelete.parsed_json?.name || t('Unknown Store')}</strong>'s {t('document file from your personal database library.')}
              </DialogContentText>

              {hasLinkedTransactions(docToDelete) ? (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#FDEDEC', border: '1px solid #FADBD8', borderRadius: 3 }}>
                  <Typography variant="subtitle2" color="error" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberIcon style={{ fontSize: 18 }} /> {t('Active Transaction Links Found!')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('This document is shared and linked with')} <strong>{docToDelete.ledger_entries.length} {t('active financial entries')}</strong> {t('in your shared ledgers.')}
                  </Typography>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={cascadeDelete}
                        onChange={(e) => setCascadeDelete(e.target.checked)}
                        color="error"
                        id="cascade-delete-checkbox"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('Also permanently DELETE all associated ledger transactions.')}
                      </Typography>
                    }
                  />

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t('If checked, transactions will be deleted. If unchecked, the transactions remain perfectly intact but the receipt attachment relationship is unlinked (severed).')}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={understandChecked}
                        onChange={(e) => setUnderstandChecked(e.target.checked)}
                        color="error"
                        id="understand-impact-checkbox"
                      />
                    }
                    label={
                      <Typography variant="body2" color="error" sx={{ fontWeight: 700 }}>
                        {t('I understand this database impact and want to proceed.')}
                      </Typography>
                    }
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('This document is not currently connected to any shared ledger entries. Deleting it is safe and will immediately free up server space.')}
                </Typography>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 2.5 }}>
              <Button onClick={handleCloseDelete} disabled={deleting} sx={{ textTransform: 'none', color: '#555' }}>
                {t('Cancel')}
              </Button>
              <Button
                onClick={handleDeleteSubmit}
                variant="contained"
                color="error"
                disabled={deleting || (hasLinkedTransactions(docToDelete) && !understandChecked)}
                startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                sx={{ borderRadius: 2, px: 3, textTransform: 'none' }}
                id="confirm-delete-button"
              >
                {deleting ? t('Deleting...') : t('Delete Permanently')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Zoom / Lightbox Dialog */}
      <Dialog
        open={zoomOpen}
        onClose={() => setZoomOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(20, 20, 20, 0.95)',
            boxShadow: 'none',
            borderRadius: 4,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)', py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('Secure Fullscreen Document Viewer')}</Typography>
          <IconButton onClick={() => setZoomOpen(false)} sx={{ color: '#fff' }}>
            &times;
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', overflow: 'hidden' }}>
          <img
            src={fileBlobUrl}
            alt="Zoomed Document"
            style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}
          />
        </DialogContent>
      </Dialog>
    </Container>
  );
}
